/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { appConfig } from './appConfig';
import { envVariables } from '../utilities/constants';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig = {
  auth: {
    clientId: appConfig.auth.clientId,
    tenantId: appConfig.auth.tenantId,
    authority: appConfig.auth.authority,
    redirectUri: appConfig.auth.redirectUri
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return;
        }
      }
    }
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const configureScopesToConsent = () => {
  const hostname = window.location.hostname;
  switch (hostname) {
    case 'localhost':
      return envVariables.local.scopesToConsent;
    case 'aiflmdev.unilever.com':
    case 'bnlwe-ai04-d-930633-webapi-02.azurewebsites.net':
      return envVariables.dev.scopesToConsent;
    case 'aiflmuat.unilever.com':
    case 'bnlwe-ai03-q-931039-webapi-02.azurewebsites.net':
      return envVariables.qa.scopesToConsent;
    case 'aiflmprod.unilever.com':
    case 'bnlwe-ai01-p-930632-webapi-02.azurewebsites.net':
      return envVariables.prod.scopesToConsent;
    default:
      return envVariables.dev.scopesToConsent;
  }
};
export const loginRequest = {
  scopes: ['User.Read'],
  extraScopesToConsent: [configureScopesToConsent()]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  profileImageEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value'
};
